"use client";

import { useEffect } from "react";
import Link from "next/link";

const CHUNK_ERROR_RE = /Loading chunk \d+ failed|error loading dynamically imported module/i;
const RELOAD_KEY = "petci_chunk_reload";

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("App route error:", error);

    // Auto-recover from stale-chunk errors (e.g. after a deploy invalidated old chunks).
    // Reload once, guarded by sessionStorage to prevent loops.
    const isChunkError =
      error.name === "ChunkLoadError" || CHUNK_ERROR_RE.test(error.message);
    if (isChunkError && typeof window !== "undefined") {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(RELOAD_KEY, "1");
        window.location.reload();
        return;
      }
    }
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(RELOAD_KEY);
    }
  }, [error]);

  return (
    <div className="max-w-lg mx-auto py-16 text-center space-y-4">
      <div className="w-14 h-14 mx-auto rounded-full bg-semantic-danger/10 flex items-center justify-center">
        <span className="text-semantic-danger text-xl font-bold">!</span>
      </div>
      <h2 className="text-lg font-display font-bold text-neutral-900">
        Something went wrong
      </h2>
      <p className="text-sm text-neutral-500">{error.message}</p>
      <div className="flex items-center justify-center gap-3 pt-2">
        <button
          onClick={reset}
          className="bg-brand-primary-hover text-white rounded-xl px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
        >
          Try again
        </button>
        <Link
          href="/home"
          className="border border-neutral-200 rounded-xl px-5 py-2.5 text-sm font-medium text-neutral-500 hover:bg-neutral-50 transition-colors"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}
